import { useNavigation } from '@react-navigation/native'
import { useTheme } from 'contexts'
import * as React from 'react'
import { useMemo, useState } from 'react'
import { Dimensions, StyleSheet, TouchableOpacity, View } from 'react-native'
import { SceneMap, TabView } from 'react-native-tab-view'

import { EmergingCoaches } from 'partials/EmergingCoaches'
import { RecentWorkouts } from 'partials/RecentWorkouts'

import { Box } from 'widgets/Box'
import { Image } from 'widgets/Image'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useDiscoverContent } from 'hooks/useDiscoverContent'
import { useEmergingCoaches } from 'hooks/useEmergingCoaches'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useInfinitePosts } from 'hooks/useInfinitePosts'

import { links } from 'utils/constants'

import { useFocusMode } from 'contexts/FocusModeContext'

import Image2023 from 'assets/2023.png'

import { CustomLazyPlaceholder } from './CustomLazyPlaceholder'
import { Leaderboard } from './Leaderboard'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scene: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabBar: {
    flexDirection: 'row',
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    margin: 6,
    marginBottom: 0,
    borderRadius: 100,
    width: 120,
    maxWidth: 120,
    height: 36,
  },
  leaderboardTab: {
    marginTop: 8,
  },
  tabView: {
    height: 250,
  },
})

const NewWorkoutsTab = () => {
  const focusMode = useFocusMode()
  const { data: recentWorkouts, isLoading: isLoadingRecentWorkouts } = useInfinitePosts({
    creatorId: focusMode.group ? focusMode.group?.trainer.id : undefined,
    limit: focusMode.group ? undefined : 25,
    order: 'created',
    filter: 'Workouts',
  })

  return (
    <View style={styles.scene}>
      <RecentWorkouts
        itemWidth={120}
        itemHeight={160}
        isFeedHeader
        recentWorkouts={recentWorkouts}
        isLoadingRecentWorkouts={isLoadingRecentWorkouts}
        focusModeProgram={focusMode?.group}
      />
    </View>
  )
}

const TopUpspacesTab = () => {
  const { data: discoverContent, isLoading: isLoadingDiscoverContent } = useDiscoverContent()
  const { data: emergingCoaches, isLoading: isLoadingEmergingCoaches } = useEmergingCoaches()

  return (
    <View style={styles.scene}>
      <EmergingCoaches
        isFeedHeader
        discoverContent={discoverContent}
        isLoadingDiscoverContent={isLoadingDiscoverContent}
        emergingCoaches={emergingCoaches}
        isLoadingEmergingCoaches={isLoadingEmergingCoaches}
      />
    </View>
  )
}

const LeaderboardTab = () => (
  <View style={styles.leaderboardTab}>
    <Leaderboard />
  </View>
)

const ChallengeTab = () => {
  const navigation = useNavigation<any>()
  const navigateToChallengeUpspace = () => {
    navigation.navigate(links.GROUP_STACK, {
      screen: links.GROUP_PAGE,
      params: { username: 'challenges' },
    })
  }

  return (
    <TouchableOpacity onPress={navigateToChallengeUpspace} style={styles.scene}>
      <Box
        width='90%'
        height='90%'
        borderRadius='sm'
        align='center'
        justifyContent='center'
        backgroundColor='v2black'
      >
        <Box
          width='100%'
          height='100%'
          align='center'
          justifyContent='center'
          padding={{ bottom: 'md' }}
        >
          <Image style={{ width: '60%', height: '50%' }} resizeMode='contain' source={Image2023} />
        </Box>

        <Box
          position='absolute'
          width='100%'
          height='100%'
          align='flex-start'
          justifyContent='flex-end'
          padding={{ bottom: 'smd', horizontal: 'md' }}
        >
          <Text color='greyLight' fontWeight='light' numberOfLines={1} fontSize='sm'>
            Sponsored by{' '}
            <Text bold color='white' fontSize='sm'>
              Athletic Greens & Hyperice
            </Text>
          </Text>
        </Box>
      </Box>
    </TouchableOpacity>
  )
}

const CustomTabBar = ({
  props,
  setIndex,
}: {
  props: unknown
  setIndex: (index: number) => void
}) => {
  const theme = useTheme()
  const navigation = useNavigation()

  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  const handlePress = (newIndex: number) => {
    if (newIndex !== props.navigationState.index) {
      setIndex(newIndex)
    } else {
      switch (newIndex) {
        case 0:
          if (!isFocusMode) {
            navigation.navigate(links.WORKOUTS_PAGE)
          } else {
            navigation.navigate(links.LEADERBOARD_PAGE, {
              trainerId: focusMode?.group?.trainer.id,
            })
          }
          break
        case 1:
          if (!isFocusMode) {
            navigation.navigate(links.TRAINERS_PAGE)
          } else {
            navigation.navigate(links.WORKOUTS_PAGE)
          }

          break
        default:
          break
      }
    }
  }

  return (
    <View style={styles.tabBar}>
      {props.navigationState.routes.map((route, i) => {
        return (
          <View
            style={[
              styles.tabItem,
              {
                backgroundColor:
                  i === props.navigationState.index
                    ? theme.colors.v2secondary
                    : theme.colors.v2midGrey,
              },
            ]}
          >
            <TouchableOpacity onPress={() => handlePress(i)}>
              <Text fontSize='xs' bold color='white'>
                {route.title}
              </Text>
            </TouchableOpacity>
          </View>
        )
      })}
    </View>
  )
}

export const FeedHeader = () => {
  const theme = useTheme()
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  const { isChallengeToggleEnabled } = useFeatureFlags()

  const [index, setIndex] = useState(0)
  const routes = !isFocusMode
    ? [
        { key: 'new-workouts', title: 'New Workouts' },
        { key: 'top-groups', title: 'Top Groups' },
      ]
    : [
        { key: 'leaderboard', title: 'Leaderboard' },
        { key: 'new-workouts', title: 'New Workouts' },
      ]

  if (isChallengeToggleEnabled) {
    routes.push({ key: 'challenges', title: 'Challenges' })
  }

  const handleIndexChange = (newIndex: number) => setIndex(newIndex)

  return (
    <View style={styles.container} key={`${isFocusMode}`}>
      <TabView
        lazy
        swipeEnabled={false}
        animationEnabled={false}
        navigationState={{ index, routes }}
        renderTabBar={(props) => <CustomTabBar props={props} setIndex={setIndex} />}
        renderScene={SceneMap({
          'new-workouts': NewWorkoutsTab,
          'top-groups': TopUpspacesTab,
          leaderboard: LeaderboardTab,
          challenges: ChallengeTab,
        })}
        renderLazyPlaceholder={({ route }) => (
          <CustomLazyPlaceholder route={route} style={styles.scene} />
        )}
        onIndexChange={handleIndexChange}
        initialLayout={{
          width: Math.min(theme.maxWidth, Dimensions.get('window').width),
        }}
        style={styles.tabView}
      />
      {isFocusMode && <Separator />}
    </View>
  )
}
